import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { 
  History,
  Mail,
  Phone,
  MessageSquare,
  Filter,
  Search,
  Eye,
  Calendar,
  User,
  CheckCircle,
  XCircle,
  Clock,
  RefreshCw
} from 'lucide-react';
import { communicationService } from '../../services/api';
import { CommunicationLog } from '../../types';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

export default function CommunicationHistory() {
  const [filters, setFilters] = useState({
    communication_type: 'all',
    status: 'all',
    date_from: '',
    date_to: '',
    search: ''
  });
  const [selectedLog, setSelectedLog] = useState<CommunicationLog | null>(null);
  const [showDetails, setShowDetails] = useState(false);

  // Récupérer l'historique des communications
  const { data: logsData, isLoading, refetch } = useQuery({
    queryKey: ['communication-logs', filters],
    queryFn: () => {
      const queryParams: any = {};
      if (filters.communication_type !== 'all') queryParams.communication_type = filters.communication_type;
      if (filters.status !== 'all') queryParams.status = filters.status;
      if (filters.date_from) queryParams.date_from = filters.date_from;
      if (filters.date_to) queryParams.date_to = filters.date_to;
      if (filters.search) queryParams.search = filters.search;
      
      return communicationService.getLogs(queryParams).then(res => res.data);
    }
  });

  const logs: CommunicationLog[] = logsData?.results || [];

  // Récupérer les statistiques
  const { data: statsData } = useQuery({
    queryKey: ['communication-stats'],
    queryFn: () => communicationService.getStats().then(res => res.data)
  });

  const stats = statsData || {
    total_messages: 0,
    emails_sent: 0,
    sms_sent: 0,
    whatsapp_sent: 0,
    success_rate: 0,
    total_cost: 0
  };

  // S'assurer que success_rate est un nombre
  const successRate = typeof stats.success_rate === 'number' ? stats.success_rate : parseFloat(stats.success_rate) || 0;

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'sent':
      case 'delivered':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'failed':
        return <XCircle className="h-4 w-4 text-red-500" />;
      case 'pending':
        return <Clock className="h-4 w-4 text-yellow-500" />;
      case 'read':
        return <CheckCircle className="h-4 w-4 text-blue-500" />;
      default:
        return <Clock className="h-4 w-4 text-gray-500" />;
    }
  };

  const getStatusLabel = (status: string) => {
    const labels: Record<string, string> = {
      'pending': 'En attente',
      'sent': 'Envoyé',
      'delivered': 'Livré',
      'read': 'Lu',
      'failed': 'Échec'
    };
    return labels[status] || status;
  };

  const getChannelIcon = (type: string) => {
    switch (type) {
      case 'email':
        return <Mail className="h-4 w-4 text-blue-500" />;
      case 'sms':
        return <Phone className="h-4 w-4 text-purple-500" />;
      case 'whatsapp':
        return <MessageSquare className="h-4 w-4 text-green-500" />;
      default:
        return <Mail className="h-4 w-4 text-gray-500" />;
    }
  };

  const handleFilterChange = (key: string, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleViewDetails = (log: CommunicationLog) => {
    setSelectedLog(log);
    setShowDetails(true);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Historique des communications</h2>
          <p className="text-gray-600">Consultez l'historique de tous les messages envoyés</p>
        </div>
        <button
          onClick={() => refetch()}
          className="flex items-center px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          Actualiser
        </button>
      </div>

      {/* Statistiques */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-lg shadow-sm border">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <History className="h-5 w-5 text-blue-600" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">Total Messages</p>
              <p className="text-lg font-semibold text-gray-900">{stats.total_messages}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow-sm border">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <CheckCircle className="h-5 w-5 text-green-600" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">Taux de Succès</p>
              <p className="text-lg font-semibold text-gray-900">{successRate.toFixed(1)}%</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow-sm border">
          <div className="flex items-center">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Phone className="h-5 w-5 text-purple-600" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">SMS Envoyés</p>
              <p className="text-lg font-semibold text-gray-900">{stats.sms_sent}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow-sm border">
          <div className="flex items-center">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <Mail className="h-5 w-5 text-yellow-600" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">Emails Envoyés</p>
              <p className="text-lg font-semibold text-gray-900">{stats.emails_sent}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filtres */}
      <div className="bg-white p-6 rounded-lg shadow-sm border">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          {/* Recherche */}
          <div className="lg:col-span-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Rechercher par patient ou contenu..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
            </div>
          </div>

          {/* Type de communication */}
          <div>
            <select
              value={filters.communication_type}
              onChange={(e) => handleFilterChange('communication_type', e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500"
            >
              <option value="all">Tous les canaux</option>
              <option value="email">Email</option>
              <option value="sms">SMS</option>
              <option value="whatsapp">WhatsApp</option>
            </select>
          </div>

          {/* Statut */}
          <div>
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500"
            >
              <option value="all">Tous les statuts</option>
              <option value="sent">Envoyé</option>
              <option value="failed">Échec</option>
              <option value="pending">En attente</option>
              <option value="delivered">Livré</option>
            </select>
          </div>

          {/* Période */}
          <div className="flex space-x-2">
            <input
              type="date"
              value={filters.date_from}
              onChange={(e) => handleFilterChange('date_from', e.target.value)}
              className="flex-1 px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
            <input
              type="date"
              value={filters.date_to}
              onChange={(e) => handleFilterChange('date_to', e.target.value)}
              className="flex-1 px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
          </div>
        </div>
      </div>

      {/* Liste des communications */}
      <div className="bg-white rounded-lg shadow-sm border">
        {logs.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <History className="h-12 w-12 mx-auto mb-4 text-gray-400" />
            <p className="text-lg font-medium mb-2">Aucun historique trouvé</p>
            <p>Aucune communication ne correspond aux critères sélectionnés</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {logs.map((log) => (
              <div key={log.id} className="p-6 hover:bg-gray-50">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-4 mb-2">
                      <div className="flex items-center space-x-2">
                        {getChannelIcon(log.communication_type)}
                        <span className="font-medium text-gray-900">
                          {log.patient_details.nom} {log.patient_details.prenoms}
                        </span>
                      </div>
                      
                      <div className="flex items-center space-x-1">
                        {getStatusIcon(log.status)}
                        <span className="text-sm font-medium">
                          {getStatusLabel(log.status)}
                        </span>
                      </div>
                      
                      <div className="text-sm text-gray-500">
                        {format(new Date(log.sent_at || log.created_at), 'dd MMM yyyy HH:mm', { locale: fr })}
                      </div>
                    </div>
                    
                    <div className="mb-2">
                      <h4 className="font-medium text-gray-800">{log.subject}</h4>
                      <p className="text-sm text-gray-600 line-clamp-2">
                        {log.message.substring(0, 150)}...
                      </p>
                    </div>
                    
                    <div className="flex items-center space-x-4 text-xs text-gray-500">
                      <span>Destinataire: {log.recipient}</span>
                      {log.template_name && (
                        <span>Template: {log.template_name}</span>
                      )}
                      {log.cost && (
                        <span>Coût: {(typeof log.cost === 'number' ? log.cost : parseFloat(log.cost) || 0).toFixed(2)} GNF</span>
                      )}
                    </div>
                  </div>
                  
                  <button
                    onClick={() => handleViewDetails(log)}
                    className="flex items-center px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
                  >
                    <Eye className="h-4 w-4 mr-1" />
                    Voir
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal des détails */}
      {showDetails && selectedLog && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowDetails(false)}></div>
            
            <div className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-2xl">
              <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium leading-6 text-gray-900">
                    Détails de la communication
                  </h3>
                  <button 
                    onClick={() => setShowDetails(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <Eye className="h-6 w-6" />
                  </button>
                </div>

                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Patient</label>
                      <p className="mt-1 text-sm text-gray-900">
                        {selectedLog.patient_details.nom} {selectedLog.patient_details.prenoms}
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Canal</label>
                      <div className="mt-1 flex items-center">
                        {getChannelIcon(selectedLog.communication_type)}
                        <span className="ml-2 text-sm text-gray-900 capitalize">
                          {selectedLog.communication_type}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Statut</label>
                      <div className="mt-1 flex items-center">
                        {getStatusIcon(selectedLog.status)}
                        <span className="ml-2 text-sm text-gray-900">
                          {getStatusLabel(selectedLog.status)}
                        </span>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Date d'envoi</label>
                      <p className="mt-1 text-sm text-gray-900">
                        {format(new Date(selectedLog.sent_at || selectedLog.created_at), 'dd MMMM yyyy à HH:mm', { locale: fr })}
                      </p>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Destinataire</label>
                    <p className="mt-1 text-sm text-gray-900">{selectedLog.recipient}</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Sujet</label>
                    <p className="mt-1 text-sm text-gray-900">{selectedLog.subject}</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Message</label>
                    <div className="mt-1 p-3 bg-gray-50 rounded-lg">
                      <p className="text-sm text-gray-900 whitespace-pre-wrap">{selectedLog.message}</p>
                    </div>
                  </div>

                  {selectedLog.error_message && (
                    <div>
                      <label className="block text-sm font-medium text-red-700">Message d'erreur</label>
                      <p className="mt-1 text-sm text-red-600">{selectedLog.error_message}</p>
                    </div>
                  )}

                  {selectedLog.template_name && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Template utilisé</label>
                      <p className="mt-1 text-sm text-gray-900">{selectedLog.template_name}</p>
                    </div>
                  )}

                  {selectedLog.cost && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Coût</label>
                      <p className="mt-1 text-sm text-gray-900">{(typeof selectedLog.cost === 'number' ? selectedLog.cost : parseFloat(selectedLog.cost) || 0).toFixed(2)} GNF</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}